import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PatternComparisonService, PatternComparison } from '@/services/PatternComparisonService';
import { PatternDetailModal } from './PatternDetailModal';
import { PatternChangeChart } from './PatternChangeChart';
import { Search, Filter, TrendingUp, TrendingDown, Equal, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface PatternComparisonTabProps {
  sessionId?: string;
  fileId?: string;
}

export const PatternComparisonTab: React.FC<PatternComparisonTabProps> = ({ 
  sessionId, 
  fileId 
}) => {
  const [patterns, setPatterns] = useState<PatternComparison[]>([]);
  const [filteredPatterns, setFilteredPatterns] = useState<PatternComparison[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<PatternComparison | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('frequency_change');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [summary, setSummary] = useState<any>(null);

  const loadComparison = async () => {
    if (!sessionId || !fileId) {
      toast.error('Session ID and File ID are required for comparison');
      return;
    }

    setLoading(true);
    try {
      console.log('Loading pattern comparison for session:', sessionId, 'file:', fileId);
      
      // First load summary to determine dataset size
      const summaryResult = await PatternComparisonService.getComparisonSummary(sessionId, fileId);
      if (summaryResult.success && summaryResult.data) {
        setSummary(summaryResult.data);
        
        // Check if this is a large dataset (estimates provided)
        const isLargeDataset = summaryResult.data.updated === 0 && summaryResult.data.unchanged === 0;
        
        if (isLargeDataset) {
          // For large datasets, show summary only and load detailed data with higher limit
          console.log('Large dataset detected - loading detailed comparison with increased limit');
          const detailedResult = await PatternComparisonService.getDetailedComparison(sessionId, fileId, 5000, 0);
          if (detailedResult.success && detailedResult.data) {
            setPatterns(detailedResult.data);
            setFilteredPatterns(detailedResult.data);
            
            if (detailedResult.hasMore) {
              toast.success(`Loaded first 5000 patterns. Total dataset: ${summaryResult.data.totalBackupPatterns + summaryResult.data.totalCurrentPatterns} patterns`);
            } else {
              toast.success(`Loaded all ${detailedResult.data.length} patterns successfully`);
            }
          }
        } else {
          // For smaller datasets, load all data at once
          const comparisonResult = await PatternComparisonService.comparePatterns(sessionId, fileId);
          if (comparisonResult.success && comparisonResult.data) {
            setPatterns(comparisonResult.data);
            setFilteredPatterns(comparisonResult.data);
          } else {
            toast.error(comparisonResult.error || 'Failed to load pattern comparison');
          }
        }
      } else {
        toast.error(summaryResult.error || 'Failed to load summary data');
      }
    } catch (error) {
      console.error('Error loading pattern comparison:', error);
      toast.error('Failed to load pattern comparison');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId && fileId) {
      loadComparison();
    }
  }, [sessionId, fileId]);

  useEffect(() => {
    let filtered = [...patterns];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pattern =>
        pattern.pattern?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pattern => pattern.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: number = 0;
      let bValue: number = 0;

      switch (sortBy) {
        case 'frequency_change':
          aValue = a.changes?.frequency ? (a.changes.frequency.after - a.changes.frequency.before) : 0;
          bValue = b.changes?.frequency ? (b.changes.frequency.after - b.changes.frequency.before) : 0;
          break;
        case 'success_rate_change':
          aValue = a.changes?.success_rate ? (a.changes.success_rate.after - a.changes.success_rate.before) : 0;
          bValue = b.changes?.success_rate ? (b.changes.success_rate.after - b.changes.success_rate.before) : 0;
          break;
        case 'backup_frequency':
          aValue = a.before?.frequency || 0;
          bValue = b.before?.frequency || 0;
          break;
        case 'current_frequency':
          aValue = a.after?.frequency || 0;
          bValue = b.after?.frequency || 0;
          break;
        default:
          return 0;
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredPatterns(filtered);
  }, [patterns, searchTerm, statusFilter, sortBy, sortOrder]);

  const getStatusBadge = (status: string) => {
    const variants = {
      created: 'bg-green-500/10 text-green-600 border-green-500/20',
      updated: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      deleted: 'bg-red-500/10 text-red-600 border-red-500/20',
      unchanged: 'bg-gray-500/10 text-gray-600 border-gray-500/20'
    };

    const icons = {
      created: <Plus className="h-3 w-3" />,
      updated: <RefreshCw className="h-3 w-3" />,
      deleted: <Trash2 className="h-3 w-3" />,
      unchanged: <Equal className="h-3 w-3" />
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.unchanged}>
        {icons[status as keyof typeof icons]}
        <span className="ml-1 capitalize">{status}</span>
      </Badge>
    );
  };

  const getChangeIcon = (change: number | null) => {
    if (!change || change === 0) return <Equal className="h-4 w-4 text-gray-400" />;
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const formatChange = (change: number | null) => {
    if (!change) return '0';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}`;
  };

  if (!sessionId || !fileId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pattern Comparison</CardTitle>
          <CardDescription>
            Select a session from the Sessions History tab to view pattern changes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No session selected for comparison
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Pattern Change Summary</CardTitle>
            <CardDescription>Overview of pattern changes in this re-analysis session</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{summary.created}</div>
                <div className="text-sm text-muted-foreground">Created</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{summary.updated}</div>
                <div className="text-sm text-muted-foreground">Updated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{summary.deleted}</div>
                <div className="text-sm text-muted-foreground">Deleted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{summary.unchanged}</div>
                <div className="text-sm text-muted-foreground">Unchanged</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{summary.totalBackupPatterns}</div>
                <div className="text-sm text-muted-foreground">Before</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{summary.totalCurrentPatterns}</div>
                <div className="text-sm text-muted-foreground">After</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Chart */}
      <PatternChangeChart patterns={patterns} />

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Pattern Details</CardTitle>
          <CardDescription>Detailed view of individual pattern changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search patterns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Changes</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="updated">Updated</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
                <SelectItem value="unchanged">Unchanged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="frequency_change">Frequency Change</SelectItem>
                <SelectItem value="success_rate_change">Success Rate Change</SelectItem>
                <SelectItem value="backup_frequency">Original Frequency</SelectItem>
                <SelectItem value="current_frequency">New Frequency</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? 'Asc' : 'Desc'}
            </Button>
          </div>

          {/* Pattern List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Loading pattern comparison...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPatterns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No patterns found matching your criteria
                </div>
              ) : (
                filteredPatterns.slice(0, 1000).map((pattern, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedPattern(pattern)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(pattern.status)}
                          <span className="text-sm font-mono text-muted-foreground">
                            Pattern: {pattern.pattern || 'N/A'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Frequency:</span>
                            <div className="flex items-center gap-1">
                              {pattern.before?.frequency || 0} → {pattern.after?.frequency || 0}
                              {getChangeIcon(pattern.changes?.frequency ? (pattern.changes.frequency.after - pattern.changes.frequency.before) : 0)}
                              <span className={`font-medium ${
                                pattern.changes?.frequency ? 
                                  ((pattern.changes.frequency.after - pattern.changes.frequency.before) > 0 ? 'text-green-600' : 
                                   (pattern.changes.frequency.after - pattern.changes.frequency.before) < 0 ? 'text-red-600' : 'text-gray-600') : 'text-gray-600'
                              }`}>
                                {formatChange(pattern.changes?.frequency ? (pattern.changes.frequency.after - pattern.changes.frequency.before) : 0)}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Success Rate:</span>
                            <div className="flex items-center gap-1">
                              {(pattern.before?.success_rate || 0).toFixed(1)}% → {(pattern.after?.success_rate || 0).toFixed(1)}%
                              {getChangeIcon(pattern.changes?.success_rate ? (pattern.changes.success_rate.after - pattern.changes.success_rate.before) : 0)}
                              <span className={`font-medium ${
                                pattern.changes?.success_rate ? 
                                  ((pattern.changes.success_rate.after - pattern.changes.success_rate.before) > 0 ? 'text-green-600' : 
                                   (pattern.changes.success_rate.after - pattern.changes.success_rate.before) < 0 ? 'text-red-600' : 'text-gray-600') : 'text-gray-600'
                              }`}>
                                {formatChange(pattern.changes?.success_rate ? (pattern.changes.success_rate.after - pattern.changes.success_rate.before) : 0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              {filteredPatterns.length > 1000 && (
                <div className="text-center py-4 text-muted-foreground">
                  Showing first 1000 of {filteredPatterns.length} patterns
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pattern Detail Modal */}
      <PatternDetailModal
        pattern={selectedPattern}
        isOpen={!!selectedPattern}
        onClose={() => setSelectedPattern(null)}
      />
    </div>
  );
};